import React, { useEffect, useMemo, useState } from "react";
import { Ship } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

export const CONTAINERS = ["20FT", "40FT", "40HC", "Reefer"];
export const CARGOS = ["General", "Bulk", "Dangerous Goods", "Reefer"];
export const CURRENCIES = ["USD", "INR", "EUR", "AED"];
export const CURRENCY_SYMBOL = { USD: "$", INR: "₹", EUR: "€", AED: "AED " };

export const money = (v, cur = "USD") =>
  `${CURRENCY_SYMBOL[cur] || ""}${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

export const ago = (iso) => {
  if (!iso) return "never";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} hr ago`;
  return `${Math.round(mins / 1440)} d ago`;
};

export const CATEGORY_STYLE = {
  api_verified: "bg-emerald-100 text-emerald-800 border-emerald-200",
  forwarder_quoted: "bg-sky-100 text-sky-800 border-sky-200",
  marketplace_indicative: "bg-amber-100 text-amber-800 border-amber-200",
  manual: "bg-slate-100 text-slate-700 border-slate-200",
  historical: "bg-violet-100 text-violet-800 border-violet-200",
};

export const CONGESTION_STYLE = {
  Normal: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Moderate: "bg-amber-100 text-amber-800 border-amber-200",
  High: "bg-orange-100 text-orange-800 border-orange-200",
  Critical: "bg-red-100 text-red-700 border-red-200",
  Unknown: "bg-slate-100 text-slate-500 border-slate-200",
};

export const RateBadge = ({ row }) => (
  <Badge variant="outline" className={`rounded-full px-2 text-[11px] ${CATEGORY_STYLE[row.rate_category] || ""}`}>
    {row.category_label}{row.is_bookable ? "" : " · indicative"}
  </Badge>
);

export const PageHead = ({ title, subtitle, right, testid }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5" data-testid={testid}>
    <div>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
        <Ship className="w-3.5 h-3.5" /> Sea Freight Intelligence
      </div>
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-head tracking-tight mt-1">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1 max-w-3xl">{subtitle}</p>}
    </div>
    {right && <div className="flex flex-wrap gap-2">{right}</div>}
  </div>
);

export const Stat = ({ label, value, hint, tone = "slate", testid }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid={testid}>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`text-2xl font-bold font-head mt-1 ${tone === "red" ? "text-red-600" : tone === "green" ? "text-emerald-600" : "text-slate-900"}`}>{value}</div>
    {hint && <div className="text-xs text-slate-400 mt-0.5">{hint}</div>}
  </div>
);

export const Empty = ({ children, testid }) => (
  <div className="bg-white border border-dashed border-slate-300 rounded-sm py-12 text-center text-sm text-slate-500" data-testid={testid}>
    {children}
  </div>
);

export function usePorts() {
  const [ports, setPorts] = useState([]);
  useEffect(() => {
    api.get("/sea-freight/ports", { params: { limit: 300 } })
      .then((r) => setPorts(r.data.ports || []))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, []);
  return ports;
}

/** Searchable port autocomplete — matches name, country, UN/LOCODE or port code. */
export function PortPicker({ ports, value, onChange, placeholder = "Search port, country or UN/LOCODE", testid }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const selected = ports.find((p) => p.id === value);
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return ports.slice(0, 12);
    return ports.filter((p) => `${p.port_name} ${p.country} ${p.unlocode} ${p.port_code}`.toLowerCase().includes(needle)).slice(0, 15);
  }, [ports, q]);

  return (
    <div className="relative">
      <Input
        data-testid={testid}
        value={open ? q : selected ? `${selected.port_name} (${selected.unlocode}) · ${selected.country}` : q}
        onFocus={() => { setOpen(true); setQ(""); }}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        placeholder={placeholder}
        className="bg-white h-9"
      />
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-sm shadow-lg">
          {value && (
            <button type="button" data-testid={`${testid}-clear`} className="w-full text-left px-3 py-2 text-xs text-slate-500 hover:bg-slate-50"
              onMouseDown={() => { onChange(""); setQ(""); setOpen(false); }}>Clear selection</button>
          )}
          {list.length === 0 && <div className="px-3 py-3 text-xs text-slate-400">No port matched</div>}
          {list.map((p) => (
            <button type="button" key={p.id} data-testid={`${testid}-opt-${p.unlocode}`}
              className="w-full text-left px-3 py-2 hover:bg-slate-50"
              onMouseDown={() => { onChange(p.id); setQ(""); setOpen(false); }}>
              <div className="text-sm text-slate-800">{p.port_name} <span className="text-slate-400">({p.unlocode})</span></div>
              <div className="text-[11px] text-slate-500">{p.country} · {p.region}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
    {children}
  </div>
);

export const pdfUrl = (path, params = {}) => {
  const p = new URLSearchParams({ token: localStorage.getItem("token"), ...params });
  return `${process.env.REACT_APP_BACKEND_URL}/api${path}?${p.toString()}`;
};
